/**
 * Anlage-Sheet fuer Abwesenheiten — Einzel (POST /absences) oder Bereich/
 * Massenbuchung (POST /absences/bulk, G-20). 409-Konflikte werden als Liste
 * im Sheet angezeigt (PRD §4.3).
 */

import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { ABSENCE_REASONS, ABSENCE_REASON_VALUES } from "@/lib/absenceColors"
import { fmtDate } from "@/lib/format"
import { cn } from "@/lib/utils"
import { api, apiConflicts, apiError } from "@/services/api"
import { BusinessDayRangePicker } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SHEET_WIDTH,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import type { AbsenceConflict, AbwesenheitGrund } from "@/types"

interface AbsenceFormSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fest vorgegebener Mitarbeiter (Personalakte-Tab); sonst Select */
  fixedEmployeeId?: string
  /** Vorbelegung des Zeitraums (z. B. aktueller Monat) */
  defaultFrom?: string
  defaultTo?: string
}

type Mode = "single" | "bulk"

export function AbsenceFormSheet({
  open,
  onOpenChange,
  fixedEmployeeId,
  defaultFrom,
  defaultTo,
}: AbsenceFormSheetProps) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<Mode>("bulk")
  const [employeeId, setEmployeeId] = useState(fixedEmployeeId ?? "")
  const [positionId, setPositionId] = useState("")
  const [from, setFrom] = useState(defaultFrom ?? "")
  const [to, setTo] = useState(defaultTo ?? "")
  const [reason, setReason] = useState<AbwesenheitGrund>("URLAUB")
  const [conflicts, setConflicts] = useState<AbsenceConflict[] | null>(null)

  // Bei jedem Oeffnen zuruecksetzen
  useEffect(() => {
    if (open) {
      setMode("bulk")
      setEmployeeId(fixedEmployeeId ?? "")
      setPositionId("")
      setFrom(defaultFrom ?? "")
      setTo(defaultTo ?? "")
      setReason("URLAUB")
      setConflicts(null)
    }
    // Vorbelegungen sind beim Oeffnen fix
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const employees = useQuery({
    queryKey: ["employees", { size: 100 }],
    queryFn: () => api.employees.list({ size: 100 }),
    enabled: open && !fixedEmployeeId,
  })

  const positions = useQuery({
    queryKey: ["employee", employeeId, "positions"],
    queryFn: () => api.employees.positions.list(employeeId),
    enabled: open && employeeId !== "",
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["absences"] })
    queryClient.invalidateQueries({ queryKey: ["employee"] })
    queryClient.invalidateQueries({ queryKey: ["employees"] })
  }

  const onError = (e: unknown) => {
    const list = apiConflicts(e)
    if (list) {
      setConflicts(list)
    } else {
      toast.error(apiError(e))
    }
  }

  const createSingle = useMutation({
    mutationFn: () =>
      api.absences.create({
        employeeId: Number(employeeId),
        employeePositionId: positionId ? Number(positionId) : null,
        startDate: from,
        endDate: to,
        reason,
      }),
    onSuccess: () => {
      invalidate()
      toast.success("Abwesenheit angelegt")
      onOpenChange(false)
    },
    onError,
  })

  const createBulk = useMutation({
    mutationFn: () =>
      api.absences.bulkCreate({
        employeeId: Number(employeeId),
        positionId: positionId ? Number(positionId) : null,
        from,
        to,
        reason,
      }),
    onSuccess: (result) => {
      invalidate()
      toast.success(
        `Abwesenheit gebucht: ${result.coveredBusinessDays} Betriebstage` +
          (result.skippedDays > 0 ? `, ${result.skippedDays} Tage übersprungen` : "")
      )
      onOpenChange(false)
    },
    onError,
  })

  const pending = createSingle.isPending || createBulk.isPending
  const valid = employeeId !== "" && from !== "" && to !== "" && from <= to

  function submit() {
    setConflicts(null)
    if (mode === "single") createSingle.mutate()
    else createBulk.mutate()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="absence-form" defaultWidth={SHEET_WIDTH.xl} className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Abwesenheit anlegen</SheetTitle>
          <SheetDescription>
            „Bereich" bucht über Betriebstage (Wochenenden/Feiertage werden übersprungen, G-20).
          </SheetDescription>
        </SheetHeader>
        <div className="grid gap-4 py-2">
          {/* Modus-Umschalter Einzel/Bereich */}
          <div className="flex gap-1">
            {(
              [
                { value: "single", label: "Einzel" },
                { value: "bulk", label: "Bereich" },
              ] as { value: Mode; label: string }[]
            ).map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setMode(m.value)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                  mode === m.value
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-transparent text-muted-foreground hover:bg-accent"
                )}
              >
                {m.label}
              </button>
            ))}
          </div>

          {!fixedEmployeeId && (
            <div className="space-y-1.5">
              <Label>Mitarbeiter *</Label>
              <Select
                value={employeeId}
                onChange={(e) => {
                  setEmployeeId(e.target.value)
                  setPositionId("")
                }}
              >
                <option value="">— Mitarbeiter wählen —</option>
                {(employees.data?.data ?? []).map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.displayName}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Stelle (optional)</Label>
            <Select
              value={positionId}
              disabled={employeeId === ""}
              onChange={(e) => setPositionId(e.target.value)}
            >
              <option value="">— keine Stelle —</option>
              {(positions.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.positionName ?? `#${p.positionId}`}
                </option>
              ))}
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Von *</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Bis *</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          <BusinessDayRangePicker
            from={from}
            to={to}
            onChange={(f, t) => {
              setFrom(f)
              setTo(t)
            }}
          />

          <div className="space-y-1.5">
            <Label>Grund *</Label>
            <Select
              value={reason}
              onChange={(e) => setReason(e.target.value as AbwesenheitGrund)}
            >
              {ABSENCE_REASON_VALUES.map((r) => (
                <option key={r} value={r}>
                  {ABSENCE_REASONS[r].label}
                </option>
              ))}
            </Select>
          </div>

          {conflicts && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
              <p className="mb-2 text-sm font-medium text-destructive">
                Der Zeitraum überlappt bestehende Abwesenheiten:
              </p>
              <ul className="space-y-1">
                {conflicts.map((c) => (
                  <li key={c.id} className="flex items-center gap-2 text-sm">
                    <Badge variant="outline" className={ABSENCE_REASONS[c.reason].badge}>
                      {ABSENCE_REASONS[c.reason].label}
                    </Badge>
                    <span>
                      {fmtDate(c.from)} – {fmtDate(c.to)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button disabled={!valid || pending} onClick={submit}>
            {mode === "bulk" ? "Bereich buchen" : "Anlegen"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
